import React, {useEffect, useState} from "react";
import {Alert, Box, Button, CardHeader, CircularProgress, Paper, Typography} from "@mui/material";
import {Refresh} from "@mui/icons-material";
import {useTranslation} from "react-i18next";
import CourierManagementLayout from "../courier/courier-management-layout";
import {useUser} from "../providers/user-context";
import useCourierDashboard from "../courier/use-courier-dashboard";
import {DashboardOrders} from "../courier/dashboard-tables";
import CourierDetailsDialog from "../courier/courier-details-dialog";
import CourierDeliveryFilters, {deliveryStatuses, deliveryStatusLabel} from "../courier/courier-delivery-filters";

function CourierDashboardContent() {
    const {tenantId} = useUser();
    const {t, i18n} = useTranslation();
    const [courier, setCourier] = useState(null);
    const [status, setStatus] = useState("");
    const [page, setPage] = useState(0), [size, setSize] = useState(20);
    const [detailsId, setDetailsId] = useState(null);
    const {data, error, loading, refresh} = useCourierDashboard(tenantId,
        // Profiles have their own page; request only the minimum legacy workload payload.
        {courierId: courier?.id, status, page, size, courierPage: 0, courierSize: 1});
    useEffect(() => {
        if (!data) return;
        if (page > Math.max(0, data.orders.totalPages - 1)) setPage(Math.max(0, data.orders.totalPages - 1));
    }, [data, page]);
    const filterStatus = value => {setStatus(value); setPage(0);};

    return <>
        <CardHeader title={t("CourierDashboard")} subheader={t("CourierDashboardDescription")}
            action={<Button startIcon={<Refresh/>} disabled={loading && !data} onClick={refresh}>{t("Refresh")}</Button>}/>
        <CourierDeliveryFilters courier={courier} status={status} onStatusChange={filterStatus}
            onClearCourier={() => {setCourier(null); setPage(0);}}/>
        {error && <Alert severity="error" action={<Button color="inherit" onClick={refresh}>{t("Retry")}</Button>}>{t(error)}</Alert>}
        {loading && !data && <CircularProgress size={24} aria-label={t("Loading")}/>}
        {data && <Box aria-busy={loading}>
            <Box sx={{display: "grid", gridTemplateColumns: {xs: "repeat(2, 1fr)", md: "repeat(6, 1fr)"}, gap: 2}}>
                {deliveryStatuses.map(value => <Paper variant="outlined" key={value} sx={{p: 2, textAlign: "center"}}>
                    <Typography color="text.secondary">{t(deliveryStatusLabel(value))}</Typography>
                    <Typography variant="h4">{data.summary.find(item => item.status === value)?.orderCount || 0}</Typography>
                </Paper>)}
            </Box>
            <Typography variant="caption">{t("CourierDashboardUpdated")}: {new Date(data.generatedAt).toLocaleString(i18n.language)}</Typography>
            <DashboardOrders data={data.orders} onDetails={setDetailsId} page={page} size={size}
                onFilterCourier={value => {setCourier(value); setPage(0);}}
                onPageChange={setPage} onSizeChange={value => {setSize(value); setPage(0);}}/>
        </Box>}
        {detailsId && <CourierDetailsDialog id={detailsId} onClose={() => {setDetailsId(null); refresh();}}/>}
    </>;
}

export default function CourierManagementPage(props) {
    return <CourierManagementLayout {...props} activeTab="dashboard"><CourierDashboardContent/></CourierManagementLayout>;
}
